<template>
  <!--
    Persistent "acting as a member" strip. Takes the same fixed top slot as
    MembershipLifecycleBanner (top:env(safe-area-inset-top); z-index:600) but is
    taller (~44px) and carries actions, so it pushes page content down via the
    `acting-as-active` html class toggled in AppLayout (see that file + the
    offset rule in src/css/app.css). Rendered on `isImpersonating` alone (no PWA
    gate) so it's a harmless safety net if a TMA session is ever impersonating.
    Mounted as a sibling of the lifecycle banner in AppLayout so it shows across
    BOTH the seller and supplier shells; the lifecycle banner is suppressed
    while impersonating so the two never contend for the top slot.

    Second reading (supplier-preview P2-A, design Section 4.3): a collector
    previewing one shop's supplier app — `isSupplierPreview && !isImpersonating`
    — gets the same strip with one line naming the shop (read by the shop owner
    over his shoulder, poolit-voice) and one button «اقفل العرض», the only way
    back to his day. Act-as wins if both were ever on.
  -->
  <div v-if="isImpersonating" class="aab-banner" role="status" aria-live="polite">
    <f7-icon f7="eye_fill" size="18px" class="aab-icon"></f7-icon>
    <div class="aab-text">
      <span class="aab-primary">
        {{ $t('actAs.banner.actingAs', { name: actingAsName || '—' }) }}
      </span>
      <span class="aab-secondary">
        {{ $t('actAs.banner.byOperator', { name: operatorName || '—' }) }}
      </span>
    </div>
    <div class="aab-actions">
      <button
        type="button"
        class="aab-btn aab-btn--ghost"
        :disabled="switching"
        @click="switchMember"
      >
        {{ $t('actAs.banner.switch') }}
      </button>
      <button type="button" class="aab-btn aab-btn--solid" :disabled="switching" @click="exit">
        {{ $t('actAs.banner.exit') }}
      </button>
    </div>
  </div>
  <div
    v-else-if="isPreview"
    class="aab-banner aab-banner--preview"
    role="status"
    aria-live="polite"
  >
    <f7-icon f7="eye_fill" size="18px" class="aab-icon"></f7-icon>
    <div class="aab-text">
      <span class="aab-primary">
        {{ $t('actAs.preview.line', { shop: previewShopName || '—' }) }}
      </span>
    </div>
    <div class="aab-actions">
      <button
        type="button"
        class="aab-btn aab-btn--solid"
        :disabled="previewBusy"
        @click="exitPreview"
      >
        {{ $t('actAs.preview.exit') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useActAs } from '@/composables/useActAs';
import { useSupplierPreview } from '@/composables/useSupplierPreview.js';

// isImpersonating drives the render; actingAsName/operatorName are the store's
// delegation names; exit/switchMember run the leave→purge→reload flow (P1).
// `switching` disables the actions during the leave→purge→reload window: the
// banner shows on every page but the full-screen switching overlay is only on
// ProfilePage, so on other shells there's no blocker — disabling prevents a
// double-tap firing a second leave()/reload while the first is in flight.
const { isImpersonating, actingAsName, operatorName, switching, exit, switchMember } = useActAs();

// The collector's per-shop supplier preview (second reading, see the template).
const {
  isActive: previewActive,
  shopName: previewShopName,
  busy: previewBusy,
  exit: exitPreview,
} = useSupplierPreview();
const isPreview = computed(() => previewActive.value && !isImpersonating.value);
</script>

<style scoped>
.aab-banner {
  position: fixed;
  top: env(
    safe-area-inset-top,
    0px
  ); /* literal env() — NOT the F7 var, which the offset rule bumps */
  left: 0;
  right: 0;
  z-index: 600; /* above F7 navbar (z-index 500), same slot as the lifecycle banner */
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  height: var(--acting-as-banner-height, 44px);
  min-height: var(--acting-as-banner-height, 44px);
  background-color: #4c1d95; /* deep violet — distinct, high-contrast operator chrome */
  color: #fff;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.aab-icon {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.95);
}

.aab-text {
  flex: 1;
  min-width: 0; /* allow the flex child to shrink so truncation works */
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1.15;
}

.aab-primary {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #fff;
}

.aab-secondary {
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgba(255, 255, 255, 0.75);
}

.aab-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.aab-btn {
  appearance: none;
  border: none;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
}

.aab-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.aab-btn--ghost {
  background-color: rgba(255, 255, 255, 0.14);
  color: #fff;
}

.aab-btn--solid {
  background-color: #fff;
  color: #4c1d95;
}
</style>
